import { useCallback, useEffect, useMemo, useState } from "react";
import { CalendarClock, History, Circle, CalendarOff, Stethoscope, Info } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { DIVISION_ORDER, fmtDuration, fmtRelativeTime } from "@/lib/constants";
import SortableHeader from "@/components/SortableHeader";
import StickyContextBar from "@/components/StickyContextBar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import SyncStickyTableHead from "@/components/table/SyncStickyTableHead";
import ActivityLoginTab from "@/components/performance/ActivityLoginTab";
import { DivisionCell, SubdivisionCell, OrgFilterBar, buildOrgOptions, matchOrg, downloadCsv, divisionLabel, subdivisionLabel } from "@/components/performance/OrgIdentity";

const DEFAULT_SORT = { key: "default", dir: "asc" };
const MONTH_ID = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];

const currentMonthLabel = () => {
  const d = new Date();
  return `${MONTH_ID[d.getMonth()]} ${d.getFullYear()}`;
};

export default function PerformancePage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState(DEFAULT_SORT);
  const [orgFilter, setOrgFilter] = useState({ q: "", divisionId: "", subdivisionId: "" });
  // 2026-09-25 (user request): tab Performa Tugas vs Aktivitas & Login (KPI).
  const [tab, setTab] = useState("tasks");
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState(null);
  const [historyBusy, setHistoryBusy] = useState(false);
  // 2026-06-11 (PERF-SDM-ATTENDANCE): agregat kehadiran (cuti/izin/sakit) per
  // staff — data ditarik dari modul Cuti & Izin (approved only). Periode
  // default = bulan berjalan; user bisa pilih bulan lain via selector.
  const [attData, setAttData] = useState(null);
  const [attBusy, setAttBusy] = useState(true);
  const now = new Date();
  const [attYear, setAttYear] = useState(now.getFullYear());
  const [attMonth, setAttMonth] = useState(now.getMonth() + 1); // 1-12; 0 = full year
  const loadAttendance = useCallback(async () => {
    setAttBusy(true);
    try {
      const { data } = await api.get("/performance/attendance", {
        params: { year: attYear, month: attMonth },
      });
      setAttData(data);
    } catch {
      setAttData({ summary: {}, per_user: [] });
    } finally { setAttBusy(false); }
  }, [attYear, attMonth]);
  useEffect(() => { loadAttendance(); }, [loadAttendance]);

  const load = () => {
    setLoading(true); setError(null);
    api.get("/performance")
      .then(({ data }) => { setData(data); setError(null); })
      .catch((e) => {
        const detail = e.response?.data?.detail;
        setError({
          status: e.response?.status || 0,
          message: typeof detail === "string" ? detail : (e.message || "Gagal memuat data performa"),
        });
      })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openHistory = async () => {
    setHistoryOpen(true);
    if (history) return; // cached
    setHistoryBusy(true);
    try {
      const { data } = await api.get("/performance/history", { params: { months: 12 } });
      setHistory(data);
    } catch (e) {
      setHistory({ months: [] });
    } finally { setHistoryBusy(false); }
  };

  const sortedRows = useMemo(() => {
    if (!data) return [];
    const arr = data.per_user.filter((u) => matchOrg(u, orgFilter));
    const { key, dir } = sort;
    const mul = dir === "asc" ? 1 : -1;
    const divOrder = (u) => DIVISION_ORDER[u.division_name] ?? 99;
    const bySub = (a, b) => (a.subdivision_name || "\uffff").localeCompare(b.subdivision_name || "\uffff");
    const byDivThenLevel = (a, b) => {
      const dv = divOrder(a) - divOrder(b) || (a.division_name || "").localeCompare(b.division_name || "");
      if (dv !== 0) return dv;
      if (a.level !== b.level) return a.level - b.level;
      return a.name.localeCompare(b.name);
    };
    if (key === "default") arr.sort(byDivThenLevel);
    else if (key === "name") arr.sort((a, b) => a.name.localeCompare(b.name) * mul);
    else if (key === "division") arr.sort((a, b) => {
      const dv = (divOrder(a) - divOrder(b) || (a.division_name || "").localeCompare(b.division_name || "")) * mul;
      if (dv !== 0) return dv;
      const sv = bySub(a, b) * mul;
      if (sv !== 0) return sv;
      if (a.level !== b.level) return a.level - b.level;
      return a.name.localeCompare(b.name);
    });
    else if (key === "level") arr.sort((a, b) => (a.level - b.level) * mul || a.name.localeCompare(b.name));
    else if (["total_assigned", "completed", "pending", "overdue", "avg_response_sec", "fastest_completion_sec", "slowest_completion_sec"].includes(key)) {
      arr.sort((a, b) => {
        const av = a[key] ?? Number.POSITIVE_INFINITY;
        const bv = b[key] ?? Number.POSITIVE_INFINITY;
        return (av - bv) * mul;
      });
    }
    return arr;
  }, [data, sort, orgFilter]);

  const orgOptions = useMemo(() => buildOrgOptions(data?.per_user || []), [data]);
  const exportCsv = () => downloadCsv(`kinerja-${new Date().toISOString().slice(0, 7)}.csv`,
    ["Nama", "Divisi", "Sub-divisi", "Level", "Total", "Selesai", "Menunggu", "Terlambat", "Avg Response", "Fastest", "Slowest"],
    sortedRows.map((u) => [u.name, divisionLabel(u), subdivisionLabel(u), `Lv${u.level}`, u.total_assigned, u.completed, u.pending, u.overdue,
      fmtDuration(u.avg_response_sec), fmtDuration(u.fastest_completion_sec), fmtDuration(u.slowest_completion_sec)]));

  if (tab === "aktivitas") {
    return (
      <div className="p-6 space-y-6" data-testid="perf-activity-page">
        <StickyContextBar
          segments={["Performance", "Report"]}
          extra={<span className="text-slate-400 normal-case">Aktivitas & Login</span>}
        />
        <div>
          <p className="overline text-slate-500">Analytics</p>
          <h1 className="font-display text-3xl tracking-tight text-[#0B1D3A]">Performance Dashboard</h1>
        </div>
        <PerfTabs tab={tab} setTab={setTab} />
        <ActivityLoginTab />
      </div>
    );
  }

  if (loading) return (
    <div className="p-8 text-sm text-slate-500" data-testid="perf-loading">
      <div className="inline-flex items-center gap-2"><Circle size={10} className="animate-pulse fill-slate-300 stroke-slate-300" /> Memuat data performa…</div>
    </div>
  );
  if (error) return (
    <div className="p-8" data-testid="perf-error">
      <div className="max-w-md bg-white border border-rose-300 border-l-4 border-l-rose-600 p-4">
        <p className="overline text-rose-700 text-[10px]">Performance Dashboard</p>
        <h2 className="font-display text-lg text-[#0B1D3A] mt-1">Data belum bisa ditampilkan</h2>
        <p className="text-xs text-slate-600 mt-2">
          Server mengembalikan <span className="font-mono">{error.status || "network error"}</span>. {error.message}
        </p>
        <p className="text-[10px] text-slate-500 mt-2 italic">Ini bukan masalah akses Anda — sesi tetap aktif. Coba muat ulang datanya.</p>
        <Button data-testid="btn-perf-retry" onClick={load} className="mt-3 rounded-none bg-[#FF5A1F] hover:bg-[#e04a13] uppercase text-xs tracking-wider font-semibold">
          Coba Lagi
        </Button>
      </div>
    </div>
  );
  if (!data) return null;

  return (
    <div className="p-6 space-y-6">
      {/* 149-cj: Compact page header — konsisten dengan halaman lain (Users,
          Collection, Accurate). Sticky pada 64–96 px. */}
      <StickyContextBar
        segments={["Performance", "Report"]}
        extra={<span className="text-slate-400 normal-case">Periode: {currentMonthLabel()}</span>}
      />

      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="overline text-slate-500">Analytics</p>
          <h1 className="font-display text-3xl tracking-tight text-[#0B1D3A]">Performance Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">
            {user.level === 1 && "Scope: Company-wide (all divisions & personnel)."}
            {user.level === 2 && `Scope: ${user.division}${user.division === "Project" ? ` · ${user.sub_division}` : ""} division (excludes Level 1).`}
            {user.level === 3 && "Scope: Tasks related to you."}
          </p>
          <p className="text-[11px] text-[#FF5A1F] mt-1 inline-flex items-center gap-1.5 font-semibold uppercase tracking-wider" data-testid="perf-current-month">
            <CalendarClock size={12} /> Periode: {currentMonthLabel()} · reset otomatis setiap awal bulan
          </p>
        </div>
        <div className="flex items-center gap-2">
          {sort.key !== "default" && (
            <button data-testid="btn-perf-reset-sort" onClick={() => setSort(DEFAULT_SORT)} className="border border-slate-300 h-9 px-3 uppercase tracking-wider font-semibold text-xs flex items-center gap-1.5 hover:bg-slate-50 text-slate-600">
              Reset Sort
            </button>
          )}
          <Button
            data-testid="btn-perf-history"
            onClick={openHistory}
            variant="outline"
            className="rounded-none border-[#0B1D3A] text-[#0B1D3A] hover:bg-[#0B1D3A] hover:text-white h-9 uppercase text-xs tracking-wider font-semibold"
          >
            <History size={13} className="mr-1.5" /> Lihat History Bulanan
          </Button>
        </div>
      </div>

      <PerfTabs tab={tab} setTab={setTab} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatBlock label="Avg Response" value={fmtDuration(data.summary.avg_response_sec)} />
        <StatBlock label="Fastest Time" value={fmtDuration(data.summary.fastest_completion_sec)} tone="emerald" />
        <StatBlock label="Slowest Time" value={fmtDuration(data.summary.slowest_completion_sec)} tone="rose" />
        <StatBlock label="Total Completed" value={data.summary.total_completed} />
      </div>

      <section className="bg-white border border-slate-200">
        <header className="px-4 py-3 border-b border-slate-200 flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-display text-lg tracking-tight">Per-Personnel Breakdown</h2>
          <OrgFilterBar options={orgOptions} filter={orgFilter} setFilter={setOrgFilter} onExport={exportCsv} />
        </header>
        <SyncStickyTableHead offset="context">
          <table className="w-full text-sm">
            {/* R74 fix: `ens-sticky-thead` dihapus — tabel ini dibungkus
                SyncStickyTableHead (JS clone); ganda = header bertumpuk. */}
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left">
                <SortableHeader label="Name" k="name" sort={sort} setSort={setSort} />
                <th className="px-4 py-3 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Status</th>
                <SortableHeader label="Divisi" k="division" sort={sort} setSort={setSort} />
                <th className="px-4 py-3 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Sub-divisi</th>
                <SortableHeader label="Level" k="level" sort={sort} setSort={setSort} />
                <SortableHeader label="Total" k="total_assigned" sort={sort} setSort={setSort} />
                <SortableHeader label="Selesai" k="completed" sort={sort} setSort={setSort} />
                <SortableHeader label="Menunggu" k="pending" sort={sort} setSort={setSort} />
                <SortableHeader label="Terlambat" k="overdue" sort={sort} setSort={setSort} />
                <SortableHeader label="Avg Response" k="avg_response_sec" sort={sort} setSort={setSort} />
                <SortableHeader label="Fastest" k="fastest_completion_sec" sort={sort} setSort={setSort} />
                <SortableHeader label="Slowest" k="slowest_completion_sec" sort={sort} setSort={setSort} />
              </tr>
            </thead>
            <tbody data-testid="performance-table">
              {sortedRows.length === 0 && (
                <tr><td colSpan={12} className="px-4 py-6 text-center text-slate-400">No data untuk bulan ini.</td></tr>
              )}
              {sortedRows.map((u) => (
                <tr key={u.user_id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium text-[#0B1D3A]">{u.name}</td>
                  <td className="px-4 py-3" data-testid={`perf-status-${u.user_id}`}>
                    {u.is_online ? (
                      <span className="inline-flex items-center gap-1.5 text-emerald-700">
                        <Circle size={8} className="fill-emerald-500 stroke-emerald-500" />
                        <span className="text-[11px] uppercase tracking-wider font-semibold">Online</span>
                      </span>
                    ) : u.last_seen_at ? (
                      <span className="inline-flex flex-col text-[11px] leading-tight">
                        <span className="inline-flex items-center gap-1.5 text-slate-500">
                          <Circle size={8} className="fill-slate-300 stroke-slate-300" />
                          <span className="uppercase tracking-wider font-semibold">Offline</span>
                        </span>
                        <span className="text-[10px] text-slate-400 mt-0.5">{fmtRelativeTime(u.last_seen_at)}</span>
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 text-slate-400">
                        <Circle size={8} className="fill-slate-200 stroke-slate-200" />
                        <span className="text-[10px] uppercase tracking-wider">Belum pernah login</span>
                      </span>
                    )}
                  </td>
                  <DivisionCell row={u} testid={`perf-division-${u.user_id}`} />
                  <SubdivisionCell row={u} testid={`perf-subdivision-${u.user_id}`} />
                  <td className="px-4 py-3"><span className="px-1.5 py-0.5 text-[10px] bg-slate-100 font-semibold uppercase tracking-wider">Lv{u.level}</span></td>
                  <td className="px-4 py-3 tabular-nums">{u.total_assigned}</td>
                  <td className="px-4 py-3 tabular-nums text-emerald-700 font-semibold">{u.completed}</td>
                  <td className="px-4 py-3 tabular-nums">{u.pending}</td>
                  <td className="px-4 py-3 tabular-nums text-rose-700">{u.overdue}</td>
                  <td className="px-4 py-3 tabular-nums">{fmtDuration(u.avg_response_sec)}</td>
                  <td className="px-4 py-3 tabular-nums text-emerald-700">{fmtDuration(u.fastest_completion_sec)}</td>
                  <td className="px-4 py-3 tabular-nums text-rose-700">{fmtDuration(u.slowest_completion_sec)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </SyncStickyTableHead>
      </section>

      {/* 2026-06-11 (PERF-SDM-ATTENDANCE): SDM · Kehadiran (Cuti / Izin / Sakit).
          Langkah pertama Performance versi SDM — data dari modul Cuti & Izin. */}
      <section className="bg-white border border-slate-200" data-testid="perf-attendance">
        <header className="px-4 py-3 border-b border-slate-200 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <CalendarOff size={16} className="text-[#FF5A1F]" />
            <h2 className="font-display text-lg tracking-tight">SDM · Kehadiran per Staff</h2>
            <span className="text-[10px] uppercase tracking-wider text-slate-500 border border-slate-300 px-1.5 py-0.5">Cuti · Izin · Sakit</span>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <select value={attMonth} onChange={(e) => setAttMonth(Number(e.target.value))}
              data-testid="perf-att-month"
              className="border border-slate-300 px-2 py-1.5 focus:outline-none focus:border-[#0B1D3A]">
              <option value={0}>Full Year</option>
              {MONTH_ID.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </select>
            <select value={attYear} onChange={(e) => setAttYear(Number(e.target.value))}
              data-testid="perf-att-year"
              className="border border-slate-300 px-2 py-1.5 focus:outline-none focus:border-[#0B1D3A]">
              {[now.getFullYear() - 2, now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map((y) =>
                <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        </header>
        {attBusy && <div className="p-4 text-xs text-slate-400 italic">Memuat data kehadiran…</div>}
        {!attBusy && attData && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 p-4 bg-slate-50/50 border-b border-slate-200">
              <MiniStat label="Total Pengajuan" value={attData.summary.total_events || 0} tone="slate" testid="perf-att-total" />
              <MiniStat label="Cuti (hari)" value={attData.summary.total_cuti_days || 0} tone="sky" testid="perf-att-cuti" />
              <MiniStat label="Izin (hari)" value={attData.summary.total_izin_days || 0} tone="amber" testid="perf-att-izin" />
              <MiniStat label="Sakit (hari)" value={attData.summary.total_sakit_days || 0} tone="rose" testid="perf-att-sakit" />
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr className="text-left uppercase tracking-wider text-[10px] text-slate-600">
                    <th className="px-4 py-2 font-semibold">Nama</th>
                    <th className="px-3 py-2 font-semibold">Divisi</th>
                    <th className="px-3 py-2 font-semibold">Sub-divisi</th>
                    <th className="px-3 py-2 font-semibold text-right">Cuti (n · hari)</th>
                    <th className="px-3 py-2 font-semibold text-right">Izin (n · hari)</th>
                    <th className="px-3 py-2 font-semibold text-right">Sakit (n · hari)</th>
                    <th className="px-3 py-2 font-semibold text-right">Total Event</th>
                    <th className="px-3 py-2 font-semibold text-right">Potongan Quota</th>
                  </tr>
                </thead>
                <tbody data-testid="perf-att-table">
                  {attData.per_user.length === 0 && (
                    <tr><td colSpan={8} className="px-4 py-6 text-center text-slate-400 italic">Belum ada data untuk periode ini.</td></tr>
                  )}
                  {attData.per_user
                    .filter((u) => matchOrg(u, orgFilter))
                    .sort((a, b) => (b.total_events - a.total_events) || a.name.localeCompare(b.name))
                    .map((u) => (
                      <tr key={u.user_id} data-testid={`perf-att-row-${u.user_id}`}
                        className={`border-b border-slate-100 hover:bg-slate-50 ${u.total_events === 0 ? "text-slate-400" : ""}`}>
                        <td className="px-4 py-2 font-medium text-[#0B1D3A]">{u.name}</td>
                        <DivisionCell row={u} className="px-3 py-2" testid={`perf-att-division-${u.user_id}`} />
                        <SubdivisionCell row={u} className="px-3 py-2" testid={`perf-att-subdivision-${u.user_id}`} />
                        <td className="px-3 py-2 tabular-nums text-right text-sky-700">{u.cuti_count > 0 ? `${u.cuti_count} · ${u.cuti_days}` : "—"}</td>
                        <td className="px-3 py-2 tabular-nums text-right text-amber-700">{u.izin_count > 0 ? `${u.izin_count} · ${u.izin_days}` : "—"}</td>
                        <td className="px-3 py-2 tabular-nums text-right text-rose-700 inline-flex items-center gap-1">
                          {u.sakit_count > 0 && <Stethoscope size={10} />}
                          {u.sakit_count > 0 ? `${u.sakit_count} · ${u.sakit_days}` : "—"}
                        </td>
                        <td className="px-3 py-2 tabular-nums text-right font-semibold">{u.total_events || "—"}</td>
                        <td className="px-3 py-2 tabular-nums text-right text-slate-600">{u.total_days_deducted > 0 ? u.total_days_deducted : "—"}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-2 text-[10px] text-slate-500 italic border-t border-slate-100 flex items-center gap-1.5">
              <Info size={11} /> Data ditarik dari modul <span className="font-semibold">Cuti &amp; Izin</span> (hanya pengajuan berstatus approved). Selanjutnya: KPI achievement per staff.
            </div>
          </>
        )}
      </section>

      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>        <DialogContent className="rounded-none max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="dialog-perf-history">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight inline-flex items-center gap-2">
              <History size={18} className="text-[#FF5A1F]" /> History Performance — 12 Bulan Terakhir
            </DialogTitle>
            <DialogDescription>
              Ringkasan task per bulan sebelum {currentMonthLabel()}. Angka dihitung dari tanggal pembuatan task.
            </DialogDescription>
          </DialogHeader>
          {historyBusy && <div className="text-sm text-slate-500 py-8 text-center">Loading history…</div>}
          {!historyBusy && history && history.months.length === 0 && (
            <div className="text-sm text-slate-500 py-8 text-center">Belum ada history bulanan.</div>
          )}
          {!historyBusy && history && history.months.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="perf-history-table">
                <thead className="bg-slate-50 border-b border-slate-200 uppercase tracking-wider text-[10px] text-slate-600">
                  <tr className="text-left">
                    <th className="px-3 py-2">Bulan</th>
                    <th className="px-3 py-2 text-right">Total</th>
                    <th className="px-3 py-2 text-right">Dikerjakan</th>
                    <th className="px-3 py-2 text-right">Batal</th>
                    <th className="px-3 py-2 text-right">Belum Dikerjakan</th>
                    <th className="px-3 py-2 text-right">Waktu Tercepat</th>
                    <th className="px-3 py-2 text-right">Waktu Terlama</th>
                    <th className="px-3 py-2 text-right">Avg Response</th>
                  </tr>
                </thead>
                <tbody>
                  {history.months.map((r) => {
                    const empty = r.total_tasks === 0;
                    return (
                      <tr key={r.month} data-testid={`perf-history-row-${r.month}`} className={`border-b border-slate-100 ${empty ? "bg-slate-50/40 text-slate-400" : "hover:bg-slate-50"}`}>
                        <td className="px-3 py-2 font-semibold text-[#0B1D3A] tabular-nums whitespace-nowrap">
                          {MONTH_ID[r.month_num - 1]} {r.year}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">{r.total_tasks || "—"}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-emerald-700 font-semibold">{r.completed || "—"}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-slate-500">{r.cancelled || "—"}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-amber-700">{r.pending || "—"}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-emerald-700">{fmtDuration(r.fastest_completion_sec) || "—"}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-rose-700">{fmtDuration(r.slowest_completion_sec) || "—"}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-slate-700">{fmtDuration(r.avg_response_sec) || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <p className="text-[10px] text-slate-500 mt-3 italic">
                * &quot;Belum Dikerjakan&quot; = task yang dibuat pada bulan tersebut namun sampai saat ini masih berstatus todo / in-progress / menunggu verifikasi. Data di-scope sesuai level akses Anda.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatBlock({ label, value, tone }) {
  const toneCls = tone === "emerald" ? "text-emerald-700" : tone === "rose" ? "text-rose-700" : "text-[#0B1D3A]";
  return (
    <div className="bg-white border border-slate-200 p-5">
      <p className="overline text-slate-500">{label}</p>
      <p className={`font-display text-3xl tabular-nums tracking-tight mt-2 ${toneCls}`}>{value}</p>
    </div>
  );
}

/** 2026-09-25 (user request): Tab bar Performance Dashboard. */
function PerfTabs({ tab, setTab }) {
  return (
    <div className="flex border-b border-slate-200" role="tablist" aria-label="Tab Performance" data-testid="perf-tabs">
      {[
        { k: "tasks", label: "Performa Tugas" },
        { k: "aktivitas", label: "Aktivitas & Login" },
      ].map((t) => (
        <button key={t.k} type="button" role="tab" aria-selected={tab === t.k}
          onClick={() => setTab(t.k)} data-testid={`perf-tab-${t.k}`}
          className={`px-4 py-2 text-xs uppercase tracking-wider font-semibold border-b-2 ${tab === t.k ? "border-[#FF5A1F] text-[#FF5A1F]" : "border-transparent text-slate-500 hover:text-[#0B1D3A]"}`}>
          {t.label}
        </button>
      ))}
    </div>
  );
}

function MiniStat({ label, value, tone, testid }) {
  const toneCls = tone === "sky" ? "text-sky-700 border-l-sky-500"
    : tone === "amber" ? "text-amber-700 border-l-amber-500"
    : tone === "rose" ? "text-rose-700 border-l-rose-500"
    : "text-[#0B1D3A] border-l-slate-400";
  return (
    <div className={`bg-white border border-slate-200 border-l-4 p-3 ${toneCls}`} data-testid={testid}>
      <p className="overline text-slate-500 leading-none">{label}</p>
      <p className={`font-display text-2xl tabular-nums tracking-tight mt-1 ${toneCls.split(" ")[0]}`}>{value}</p>
    </div>
  );
}
